import { useState, useEffect, useCallback } from 'react';
import { NotificationToast } from '../../../../components/organisms/notification-toast';
import Modal, { ModalProps } from '../../../../components/template/mvc-modal';
import Toggle from '../../../../components/molecules/toggle';
import { useTranslation } from 'react-i18next';
import { EditableChangeEvent } from '../../../../utils/types/editable';
import Input from '../../../../components/atoms/input';
import ClientService, { IClient } from '../../../../services/Client.service';
import { useRouteLoaderData } from 'react-router-dom';
import Select from '../../../../components/atoms/select';
import { IUser } from '../../../../services/User.service';
import ShipService, { IShip } from '../../../../services/Ship.service';
import { HomeLoaderData } from '../../../../types/homeLoaderData';

interface ShipsModalProps extends ModalProps<IShip> {
	setModalProps: React.Dispatch<React.SetStateAction<ModalProps<IShip>>>;
}

const ShipsModal = ({
	data,
	setModalProps,
	onClose,
	mode,
	isModalVisible,
	...props
}: ShipsModalProps) => {
	const { t } = useTranslation();

	const { user, accountId } = useRouteLoaderData('home') as HomeLoaderData;

	const [error, setError] = useState<string>('');

	useEffect(() => {
		if (!error) return;
		NotificationToast.Warn(t(error));
		setError('');
	}, [error]);

	const onSubmit = () => {
		if (!validate()) return;
		if (mode === 'edit') {
			onEdit();
			return;
		}
		onCreate();
	};

	const validate = (): boolean => {
		if (!data.name || !data.owner) {
			setError('alert.mandatory');
			return false;
		}
		return true;
	};

	const onCreate = async () => {
		const response = await ShipService.create(
			{ ...data, client: { id: Number(accountId) } },
			setError
		);
		if (!response) return;
		NotificationToast.Success(t('alert.success'));
		onClose(true);
	};

	const onEdit = async () => {
		const response = await ShipService.edit(
			{ ...data, client: { id: Number(accountId) } },
			setError
		);
		if (!response) return;
		NotificationToast.Success(t('alert.success'));
		onClose(true);
	};

	const handleInput = (
		e: React.ChangeEvent<HTMLInputElement> | EditableChangeEvent
	) => {
		setModalProps((prev) => ({
			...prev,
			data: {
				...prev.data,
				[e.target.name]: e.target.value,
			},
		}));
	};

	const renderToggles = () => (
		<div className="grid">
			<Toggle
				name="isActive"
				value={data.isActive}
				label={t('status.active')}
				onChange={handleInput}
				disabled={mode === 'view'}
			/>
		</div>
	);

	const renderInputs = () => (
		<div className="grid gap-x-2 gap-y-3">
			<Input
				value={data.name}
				name="name"
				label={t('port_management.ship')}
				placeholder={t('port_management.ship')}
				onChange={handleInput}
				disabled={mode === 'view'}
				required
			/>
			<Input
				value={data.owner}
				name="owner"
				label={t('port_management.ships.owner')}
				placeholder={t('port_management.ships.owner')}
				onChange={handleInput}
				disabled={mode === 'view'}
				required
			/>
		</div>
	);

	return (
		<Modal<IShip>
			onClose={onClose}
			data={data}
			mode={mode}
			onConfirm={onSubmit}
			isModalVisible={isModalVisible}
			{...props}
		>
			{renderToggles()}
			{renderInputs()}
		</Modal>
	);
};

export default ShipsModal;
